
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Client } from "@/models/Client";
import ClientCard from "./ClientCard";
import ClientCardSkeleton from "./ClientCardSkeleton";
import ClientDeleteDialog from "./ClientDeleteDialog";
import ClientUpcomingBookingsDialog from "./ClientUpcomingBookingsDialog";
import SessionHistoryDialog from "./SessionHistoryDialog";
import { useTranslation } from "react-i18next";

interface ClientsListProps {
  clients: Client[];
  onEditClient: (client: Client) => void;
  isLoading?: boolean;
  canManage?: boolean;
  onClientClick?: (client: Client) => void;
}

const ClientsList: React.FC<ClientsListProps> = ({
  clients,
  onEditClient,
  isLoading = false,
  canManage = false,
  onClientClick,
}) => {
  const { t } = useTranslation();
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [clientForUpcomingBookings, setClientForUpcomingBookings] = useState<Client | null>(null);
  const [clientForSessionHistory, setClientForSessionHistory] = useState<Client | null>(null);

  // Use clients as they come from the backend (already sorted correctly)
  const sortedClients = clients;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <ClientCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sortedClients.length === 0 ? (
        <Card className="dark:border-gray-700">
          <CardContent className="p-6">
            <div className="text-center text-muted-foreground greek-text">
              {t('clients.noClientsFound')}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedClients.map((client) => (
            <div key={client.id} data-client-id={client.id}>
              <ClientCard
                client={client}
                onEdit={onEditClient}
                onDelete={canManage ? setClientToDelete : undefined}
                onViewUpcomingBookings={(client) => {
                  console.log('Setting client for upcoming bookings:', client);
                  setClientForUpcomingBookings(client);
                }}
                onViewSessionHistory={setClientForSessionHistory}
                canManage={canManage}
                onClientClick={onClientClick}
              />
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <ClientDeleteDialog
          client={clientToDelete}
          open={!!clientToDelete}
          onOpenChange={(open) => !open && setClientToDelete(null)}
        />
      )}

      <ClientUpcomingBookingsDialog
        client={clientForUpcomingBookings}
        open={!!clientForUpcomingBookings}
        onOpenChange={(open) => !open && setClientForUpcomingBookings(null)}
      />

      <SessionHistoryDialog
        open={!!clientForSessionHistory}
        onOpenChange={(open) => !open && setClientForSessionHistory(null)}
        clientId={clientForSessionHistory?.id || ''}
        clientName={clientForSessionHistory ? `${clientForSessionHistory.firstName} ${clientForSessionHistory.lastName}` : ''}
      />
    </div>
  );
};

export default ClientsList;
